import { useMemo, useState } from "react";
import {
  buildFundCommunityRewardsMsg,
  buildHarvestMsg,
  buildRegisterMarketMsg,
  buildRegisterMarketsBatch,
  buildWithdrawTreasuryMsg,
  type CosmosMsg,
  type EnrichedMarket,
} from "@axiome/counter-lp-sdk";
import { VAULT_ADDRESS } from "../lib/config";
import { friendlyError } from "../lib/friendlyError";

const REGISTER_CHUNK = 8;

export function AdminPanel(props: {
  markets: EnrichedMarket[];
  registeredCount?: number;
  account: string | null;
  isAdmin: boolean;
  onSign: (msgs: CosmosMsg[]) => Promise<void>;
}) {
  const [symbol, setSymbol] = useState("");
  const [pool, setPool] = useState("");
  const [token, setToken] = useState("");
  const [decimals, setDecimals] = useState("6");
  const [fundSymbol, setFundSymbol] = useState("");
  const [fundAmount, setFundAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [chunkIdx, setChunkIdx] = useState(0);

  const selectedFund = useMemo(
    () => props.markets.find((m) => m.symbol === fundSymbol),
    [fundSymbol, props.markets],
  );

  const registerChunks = useMemo(() => {
    const chunks: EnrichedMarket[][] = [];
    for (let i = 0; i < props.markets.length; i += REGISTER_CHUNK) {
      chunks.push(props.markets.slice(i, i + REGISTER_CHUNK));
    }
    return chunks;
  }, [props.markets]);

  async function run(label: string, fn: () => Promise<void>) {
    if (!props.account) {
      setStatus("Сначала подключите кошелёк администратора");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      setStatus(`${label}: подтвердите действие в Axiome Wallet`);
    } catch (e) {
      setStatus(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  function pickFromMarket(m: EnrichedMarket) {
    setSymbol(m.symbol);
    setPool(m.pool);
    setToken(m.token);
    setDecimals(String(m.tokenDecimals));
    setFundSymbol(m.symbol);
  }

  if (!props.isAdmin) {
    return (
      <div className="section">
        <h2>Управление</h2>
        <p className="lead">
          Доступ только для кошелька администратора. Подключите его через Axiome Connect.
        </p>
        {!props.account ? (
          <p className="hint">Кошелёк ещё не подключён.</p>
        ) : (
          <p className="hint" style={{ color: "var(--danger)" }}>
            Этот адрес не является администратором.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="section">
      <h2>Управление</h2>
      <p className="lead">
        Комиссии снимаются автоматически при получении дохода пользователями. Здесь вы
        регистрируете пары и выводите долю казны. Запас токенов поставляют проекты во вкладке
        «Проект».
      </p>

      <div className="admin-block">
        <h3>Зарегистрировать пары от $100</h3>
        <p className="hint">
          Готово к добавлению: {props.markets.length} пар. Уже в сервисе:{" "}
          {props.registeredCount ?? "—"}. По {REGISTER_CHUNK} за одну подпись.
        </p>
        <p className="hint">
          Группа {Math.min(chunkIdx + 1, Math.max(registerChunks.length, 1))} /{" "}
          {registerChunks.length || 1}
          {registerChunks[chunkIdx]
            ? `: ${registerChunks[chunkIdx].map((m) => m.symbol).join(", ")}`
            : ""}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn btn-primary"
            disabled={busy || !registerChunks[chunkIdx]}
            onClick={() =>
              run(`Группа ${chunkIdx + 1}`, async () => {
                if (!props.account || !registerChunks[chunkIdx]) return;
                await props.onSign(
                  buildRegisterMarketsBatch({
                    sender: props.account,
                    vault: VAULT_ADDRESS,
                    markets: registerChunks[chunkIdx],
                  }),
                );
                setChunkIdx((i) => Math.min(i + 1, Math.max(registerChunks.length - 1, 0)));
              })
            }
          >
            Подписать группу {chunkIdx + 1}
          </button>
          <button
            className="btn btn-ghost"
            disabled={busy || chunkIdx <= 0}
            onClick={() => setChunkIdx((i) => Math.max(0, i - 1))}
          >
            ←
          </button>
          <button
            className="btn btn-ghost"
            disabled={busy || chunkIdx >= registerChunks.length - 1}
            onClick={() =>
              setChunkIdx((i) => Math.min(registerChunks.length - 1, i + 1))
            }
          >
            →
          </button>
        </div>
      </div>

      <div className="admin-block">
        <h3>Выбрать пару из списка</h3>
        <div className="field">
          <label>Рынок</label>
          <select
            value={fundSymbol}
            onChange={(e) => {
              const m = props.markets.find((x) => x.symbol === e.target.value);
              setFundSymbol(e.target.value);
              if (m) pickFromMarket(m);
            }}
          >
            <option value="">—</option>
            {props.markets.map((m) => (
              <option key={m.id} value={m.symbol}>
                {m.symbol} · ${m.tvlUsd.toFixed(0)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-block">
        <h3>Зарегистрировать вручную</h3>
        <div className="field">
          <label>Тикер</label>
          <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} />
        </div>
        <div className="field">
          <label>Адрес пула</label>
          <input value={pool} onChange={(e) => setPool(e.target.value)} />
        </div>
        <div className="field">
          <label>Адрес токена</label>
          <input value={token} onChange={(e) => setToken(e.target.value)} />
        </div>
        <div className="field">
          <label>Знаков после запятой</label>
          <input value={decimals} onChange={(e) => setDecimals(e.target.value)} />
        </div>
        <button
          className="btn btn-primary"
          disabled={busy || !symbol || !pool || !token}
          onClick={() =>
            run("Регистрация", async () => {
              if (!props.account) return;
              await props.onSign([
                buildRegisterMarketMsg({
                  sender: props.account,
                  vault: VAULT_ADDRESS,
                  symbol,
                  pool,
                  token,
                  tokenDecimals: Number(decimals) || 6,
                }),
              ]);
            })
          }
        >
          Зарегистрировать
        </button>
      </div>

      <div className="admin-block">
        <h3>Снять комиссии и пополнить бонус</h3>
        <p className="hint">
          Снятие комиссий может вызвать любой адрес — при получении дохода пользователями оно
          запускается само. Здесь можно сделать это вручную или пополнить LP Bonus.
        </p>
        <button
          className="btn btn-primary"
          disabled={busy || !selectedFund}
          onClick={() =>
            run("Снятие комиссий", async () => {
              if (!props.account || !selectedFund) return;
              await props.onSign([
                buildHarvestMsg({
                  sender: props.account,
                  vault: VAULT_ADDRESS,
                  symbol: selectedFund.symbol,
                }),
              ]);
            })
          }
        >
          Снять комиссии {selectedFund?.symbol ?? ""}
        </button>
        <div className="field" style={{ marginTop: 12 }}>
          <label>Пополнить LP Bonus (AXM)</label>
          <input
            value={fundAmount}
            onChange={(e) => setFundAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="1000"
          />
        </div>
        <button
          className="btn btn-ghost"
          disabled={busy || !selectedFund || !fundAmount}
          onClick={() =>
            run("LP Bonus", async () => {
              if (!props.account || !selectedFund) return;
              await props.onSign([
                buildFundCommunityRewardsMsg({
                  sender: props.account,
                  vault: VAULT_ADDRESS,
                  symbol: selectedFund.symbol,
                  amountHuman: fundAmount,
                }),
              ]);
            })
          }
        >
          Пополнить LP Bonus
        </button>
      </div>

      <div className="admin-block">
        <h3>Вывести долю казны</h3>
        <p className="hint">
          Доступна только доля казны. Запас токенов проектов и накопления пользователей
          остаются на месте.
        </p>
        <div className="field">
          <label>Сумма AXM (пусто = всё доступное)</label>
          <input
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="всё доступное"
          />
        </div>
        <button
          className="btn btn-danger"
          disabled={busy}
          onClick={() =>
            run("Вывод казны", async () => {
              if (!props.account) return;
              await props.onSign([
                buildWithdrawTreasuryMsg({
                  sender: props.account,
                  vault: VAULT_ADDRESS,
                  amountHuman: withdrawAmount || undefined,
                }),
              ]);
            })
          }
        >
          Вывести казну
        </button>
      </div>

      {status ? <p className="status">{status}</p> : null}
    </div>
  );
}
